import React, { useState, useEffect } from 'react';
import { FaEnvelope, FaExclamationTriangle, FaSpinner, FaCheckCircle, FaTimes } from 'react-icons/fa';
import { AuthData } from '../provider/AuthProvider';

const EmailVerificationBanner = ({ 
  showOnlyIfUnverified = true, 
  dismissible = true,
  className = "" 
}) => {
  const { user, sendVerificationEmail, checkEmailVerification } = AuthData();
  const [isVisible, setIsVisible] = useState(true);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');
  const [isChecking, setIsChecking] = useState(false);

  // Don't show if user is not authenticated or if email is already verified (when showOnlyIfUnverified is true)
  if (!user?.isAuthenticated || (showOnlyIfUnverified && user?.emailVerified)) {
    return null;
  }

  // Don't show if manually dismissed and dismissible
  if (!isVisible && dismissible) {
    return null;
  }

  const handleResendEmail = async () => {
    setIsResending(true);
    setResendError('');
    setResendSuccess(false);

    try {
      await sendVerificationEmail();
      setResendSuccess(true);
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        setResendSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Error resending verification email:', error);
      setResendError(error.message || 'Failed to send verification email');
    } finally {
      setIsResending(false);
    }
  };

  const handleCheckVerification = async () => {
    setIsChecking(true);
    try {
      const isVerified = await checkEmailVerification();
      if (isVerified) {
        // Email is now verified, the auth state will update automatically
        setIsVisible(false);
      }
    } catch (error) {
      console.error('Error checking verification status:', error);
    } finally {
      setIsChecking(false);
    }
  };

  const handleDismiss = () => {
    if (dismissible) {
      setIsVisible(false);
    }
  };

  // If email is verified, show success message briefly
  if (user?.emailVerified) {
    return (
      <div className={`bg-green-50 border border-green-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-center">
          <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="font-semibold text-green-900">Email Verified!</h4>
            <p className="text-green-800 text-sm">Your email address has been successfully verified.</p>
          </div>
          {dismissible && (
            <button
              onClick={handleDismiss}
              className="text-green-600 hover:text-green-800 ml-2"
            >
              <FaTimes />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-yellow-50 border border-yellow-200 rounded-lg p-4 ${className}`}>
      <div className="flex items-start">
        <FaExclamationTriangle className="text-yellow-500 mr-3 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h4 className="font-semibold text-yellow-900 mb-1">Email Verification Required</h4>
          <p className="text-yellow-800 text-sm mb-3">
            Please verify your email address ({user?.userd?.email}) to access all features.
          </p>
          
          {resendSuccess && (
            <div className="bg-green-100 border border-green-300 rounded p-2 mb-3">
              <div className="flex items-center text-green-800 text-sm">
                <FaCheckCircle className="mr-2" />
                Verification email sent! Check your inbox and spam folder.
              </div>
            </div>
          )}

          {resendError && (
            <div className="bg-red-100 border border-red-300 rounded p-2 mb-3">
              <p className="text-red-800 text-sm">{resendError}</p>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleResendEmail}
              disabled={isResending}
              className="bg-yellow-500 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isResending ? (
                <>
                  <FaSpinner className="animate-spin mr-1" />
                  Sending...
                </>
              ) : (
                <>
                  <FaEnvelope className="mr-1" />
                  Resend Email
                </>
              )}
            </button>

            <button
              onClick={handleCheckVerification}
              disabled={isChecking}
              className="bg-blue-500 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isChecking ? (
                <>
                  <FaSpinner className="animate-spin mr-1" />
                  Checking...
                </>
              ) : (
                'I\'ve Verified'
              )}
            </button>
          </div>

          <p className="text-yellow-700 text-xs mt-2">
            Can't find the email? Check your spam folder or try resending.
          </p>
        </div>
        
        {dismissible && (
          <button
            onClick={handleDismiss}
            className="text-yellow-600 hover:text-yellow-800 ml-2 flex-shrink-0"
          >
            <FaTimes />
          </button>
        )}
      </div>
    </div>
  );
};

export default EmailVerificationBanner;